import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getCheckoutData,
  createOfflineBooking,
} from "../../../../services/offlineBookingService";
import { list } from "../../../../services/packageService";
import Toster from "../../../common/Toster";
import Dialog from "../../../dialog/Dialog";

const OfflineBooking = () => {
  const [formData, setFormData] = useState({
    package_id: "",
    stay_category_id: "",
    booking_date: "",
    adult_count: 1,
    child_count: 0,
    infant_count: 0,
    package_price_rate_id: "",
    firstName: "",
    email: "",
    phone: "",
    total_amount: 0,
    discount_amount: 0,
  });

  const [packageDetails, setPackageDetails] = useState(null);
  const [isLoadingPackage, setIsLoadingPackage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch packages
  const { data: packagesData } = useQuery({
    queryKey: ["packages"],
    queryFn: () => list(1),
  });

  const packages = packagesData?.data?.data?.data || [];
  // Map packages with their group names
  const allPackages = packages.flatMap((group) =>
    (group.packages || []).map((pkg) => ({
      ...pkg,
      groupName: group.name,
      groupId: group.id,
    }))
  );

  // Fetch package details when form data changes
  useEffect(() => {
    const fetchCheckoutDetails = async () => {
      if (
        formData.package_id &&
        formData.stay_category_id &&
        formData.booking_date &&
        formData.package_price_rate_id
      ) {
        try {
          setIsLoadingPackage(true);
          const response = await getCheckoutData({
            package_id: formData.package_id,
            stay_category_id: formData.stay_category_id,
            booking_date: formData.booking_date,
            adult_count: formData.adult_count,
            child_count: formData.child_count,
            infant_count: formData.infant_count,
            package_price_rate_id: formData.package_price_rate_id,
          });

          if (response.status) {
            setPackageDetails(response.data);
            setFormData((prev) => ({
              ...prev,
              total_amount: response.data.total_price ?? 0,
              discount_amount: response.data.discount_price ?? 0,
            }));
          }
        } catch (error) {
          console.error("Error fetching package details:", error);
          setErrorMessage("Failed to load package details");
          setShowError(true);
        } finally {
          setIsLoadingPackage(false);
        }
      }
    };

    fetchCheckoutDetails();
  }, [
    formData.package_id,
    formData.stay_category_id,
    formData.booking_date,
    formData.adult_count,
    formData.child_count,
    formData.infant_count,
    formData.package_price_rate_id,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes("_count") ? parseInt(value) || 0 : value,
    }));
  };

  const handlePackageChange = (e) => {
    const packageId = e.target.value;
    const selectedPackage = allPackages.find(
      (pkg) => pkg.id === parseInt(packageId)
    );

    setFormData((prev) => ({
      ...prev,
      package_id: packageId,
      stay_category_id: selectedPackage?.default_stay_category_id || "",
      package_price_rate_id: "",
    }));
    setPackageDetails(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowError(false);
    setErrorMessage("");

    try {
      const bookingData = {
        package_id: formData.package_id,
        stay_category_id: formData.stay_category_id,
        booking_date: formData.booking_date,
        adult_count: formData.adult_count,
        child_count: formData.child_count,
        infant_count: formData.infant_count,
        package_price_rate_id: formData.package_price_rate_id,
        total_amount: formData.total_amount,
        discount_amount: formData.discount_amount,
        name: formData.firstName,
        email: formData.email,
        phone: formData.phone,
      };

      const response = await createOfflineBooking(bookingData);

      if (response.status) {
        setSuccessMessage("Offline booking created successfully!");
        setShowSuccess(true);
        // Reset form
        setFormData({
          package_id: "",
          stay_category_id: "",
          booking_date: "",
          adult_count: 1,
          child_count: 0,
          infant_count: 0,
          package_price_rate_id: "",
          firstName: "",
          email: "",
          phone: "",
          total_amount: 0,
          discount_amount: 0,
        });
        setPackageDetails(null);
      } else {
        setErrorMessage(response.message || "Failed to create booking");
        setShowError(true);
      }
    } catch (error) {
      console.error("Booking error:", error);
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to create booking. Please try again."
      );
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculatePrices = () => {
    if (!packageDetails) return { baseTotal: 0, total: 0 };

    const {
      adult_price,
      child_price,
      infant_price,
      discount_price,
      final_price,
    } = packageDetails;
    const adults = formData.adult_count || 0;
    const children = formData.child_count || 0;
    const infants = formData.infant_count || 0;

    const baseTotal =
      adults * parseFloat(adult_price || 0) +
      children * parseFloat(child_price || 0) +
      infants * parseFloat(infant_price || 0);

    const total = final_price || baseTotal;

    return {
      baseTotal: Math.round(baseTotal),
      total: Math.round(total),
    };
  };

  const prices = calculatePrices();

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Create Offline Booking
        </h1>
        <p className="text-gray-600 mt-1">
          Add a new offline booking for a customer
        </p>
      </div>

      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={5000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && (
        <Toster
          message={successMessage}
          type="success"
          duration={5000}
          onClose={() => setShowSuccess(false)}
        />
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Package Selection */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">
              Package Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Package <span className="text-red-500">*</span>
                </label>
                <select
                  name="package_id"
                  value={formData.package_id}
                  onChange={handlePackageChange}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Package</option>
                  {allPackages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.groupName}{" "}
                      {pkg.tour_type
                        ? `(${pkg.tour_type.replace(/_/g, " ")})`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Booking Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="booking_date"
                  value={formData.booking_date}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {formData.package_id && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Stay Category <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={packageDetails?.stay_category_name || "Loading..."}
                      disabled
                      className="w-full h-12 px-4 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Price Rate <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="package_price_rate_id"
                      value={formData.package_price_rate_id}
                      onChange={handleInputChange}
                      required
                      className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select Rate</option>
                      {packageDetails?.rate_date_ranges?.map((rate) => (
                        <option key={rate.id} value={rate.id}>
                          {rate.rate_type === "normal"
                            ? "Normal Rate"
                            : rate.rate_type === "seasonal"
                            ? "Seasonal Rate"
                            : "Peak Rate"}{" "}
                          - {rate.start_date} to {rate.end_date}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {/* Travelers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Adults <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="adult_count"
                  value={formData.adult_count}
                  onChange={handleInputChange}
                  required
                  min="1"
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Children
                </label>
                <input
                  type="number"
                  name="child_count"
                  value={formData.child_count}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Infants
                </label>
                <input
                  type="number"
                  name="infant_count"
                  value={formData.infant_count}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="space-y-4 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">
              Customer Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter customer email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </div>

          {/* Price Summary */}
          {packageDetails && (
            <div className="space-y-4 border-t pt-6">
              <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Price Summary
              </h2>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900 font-semibold">
                    ₹{prices.baseTotal.toLocaleString()}
                  </span>
                </div>
                {Number(packageDetails.gst_amount) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      GST ({packageDetails.gst_percent ?? 5}%)
                    </span>
                    <span className="text-gray-900 font-semibold">
                      ₹{Math.round(Number(packageDetails.gst_amount)).toLocaleString()}
                    </span>
                  </div>
                )}
                {packageDetails.discount_percentage > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700">
                      Discount ({packageDetails.discount_percentage}%)
                    </span>
                    <span className="text-green-700 font-semibold">
                      -₹{packageDetails.discount_price?.toLocaleString() || 0}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-900 font-bold">Total Amount</span>
                  <span className="text-primary-600 font-bold text-lg">
                    ₹{prices.total.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setFormData({
                  package_id: "",
                  stay_category_id: "",
                  booking_date: "",
                  adult_count: 1,
                  child_count: 0,
                  infant_count: 0,
                  package_price_rate_id: "",
                  firstName: "",
                  email: "",
                  phone: "",
                  total_amount: 0,
                  discount_amount: 0,
                });
                setPackageDetails(null);
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isLoadingPackage}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating...
                </>
              ) : (
                "Create Offline Booking"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OfflineBooking;
